import React from "react";
import {minDb, nHarmonics} from "./spectra";
import * as d3 from "d3";
import scaleDb from "./scaleDb";
import {HorizontalScale, useChartOptions, VerticalScale} from "./ChartOptions";

export interface SpectrumProps {
    /** The harmonics array from the spectra database */
    harmonics: readonly number[],
    /** The display name of the wave, or undefined to not display a name */
    name?: string,
    /** The URL to link to, or undefined for no link */
    url?: string,
}

const margin = {
    top: 16,
    bottom: 25,
    left: 28,
    right: 18,
};
const width = 800;
const height = 200;

/** Turn a number of dB into a label with units.
 *
 * Uses the maths minus sign instead of hyphen, and the infinity sign when appropriate.
 */
function formatDecibels(dB: number): string {
    if (dB === -Infinity) {
        return "−∞ dB";
    } else {
        return `−${(-dB).toFixed(1)} dB`;
    }
}

/** Is this harmonic number a major tick?
 * This is not major in the musical sense, but the major tick/minor tick distinction for the chart.
 */
function isMajorHarmonic(n: number): boolean {
    return (n === 1 || (n % 10) === 0);
}

/* tickFormat has the wrong type in the official typings: it's allowed to return null for no label. */
const xHarmonics = d3.scaleLinear([1, nHarmonics], [margin.left, width - margin.right]);
xHarmonics.tickFormat = function() {
    /* but null out the labels for all but 1 and every 10th */
    return (n: d3.NumberValue): string => (isMajorHarmonic(n.valueOf()) ? n.toString() : null as any)
}

const xIntervals = d3.scaleLog([1, nHarmonics], [margin.left, width - margin.right]).base(2);
xIntervals.tickFormat = function() {
    return (input: d3.NumberValue): string => {
        const n = input.valueOf();
        if (n === 0) {
            return "fundamental";
        }
        if ((n > 16 && n % 8 !== 0) || (n > 6 && n % 2 !== 0)) {
            return null as any;
        }
        const octaves = Math.log2(n);
        if (Number.isInteger(octaves)) {
            return `${octaves} oct`;
        }
        return (octaves * 12).toFixed(0);
    }
}

const yIndB = d3.scaleLinear([minDb, 0], [height - margin.bottom, margin.top]).clamp(true);
const yAmplitude = scaleDb([minDb, 0], [height - margin.bottom, margin.top]).clamp(true);

function xAxis(g: d3.Selection<any, any, any, any>, x: d3.AxisScale<d3.NumberValue>): void {
    g
        .attr("transform", `translate(0, ${height - margin.bottom})`)
        .transition()
        .call(d3.axisBottom(x)
            /* generate the same number of ticks as there are harmonics */
            .ticks(nHarmonics)
        )
        .attr("class", "x-axis");
}

function yAxis(g: d3.Selection<any, any, any, any>, y: d3.AxisScale<d3.NumberValue>): void {
    const axis = g
        .attr("transform", `translate(${margin.left},0)`);
    axis.transition()
        .call(d3.axisLeft(y)
            .tickValues(y === yIndB ? [0, -6, -12, -24, -48, -72] : [0, -3, -6, -12, -24, -80])
            .tickSizeOuter(0)
        )
        .attr("class", "y-axis");
    axis.select(".domain").remove();
    axis.selectAll(".tick line").attr("x1", xHarmonics(nHarmonics)! - xHarmonics(1)!).attr("stroke", "black");
}

export function Spectrum(props: SpectrumProps): React.ReactElement {
    /* defaults */
    const {horizontalScale, verticalScale} = useChartOptions();

    const xRef = React.useRef<SVGGElement>(null);
    const yRef = React.useRef<SVGGElement>(null);
    const chartContent = React.useRef<SVGGElement>(null);
    const xScale = horizontalScale === HorizontalScale.harmonics ? xHarmonics : xIntervals;
    const yScale = verticalScale === VerticalScale.dB ? yIndB : yAmplitude;

    React.useEffect(() => {
        /* draw the axes */
        d3.select(xRef.current).call(xAxis, xScale);
        d3.select(yRef.current).call(yAxis, yScale);
    }, [xScale, yScale]);

    /* This crazy pair of useEffects draws the chart with no transition if the harmonic array is replaced,
     * or with a transition if the scales/axes are replaced
     */
    React.useEffect(() => {
        /* draw the chart bars */
        d3.select(chartContent.current)
            .selectAll("rect.bar")
            /* need to cast away readonly */
            .data(props.harmonics as number[])
            .join("rect")
            .attr("class", "bar")
            .attr("width", 2)
            .attr("x", (_d: number, i: number) => (xScale(i + 1)! - 0.5))
            .attr("y", (d: number) => yScale(d)!)
            .attr("height", (d: number) => (yScale(minDb)! - yScale(d)!))
        /* don't use xScale and yScale as deps, they are handled by the useEffect below */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.harmonics]);

    React.useEffect(() => {
        /* redraw the chart bars, with a transition */
        d3.select(chartContent.current)
            /* these type args would be inferred from the .data call */
            .selectAll<SVGGElement, number>("rect.bar")
            .join("rect")
            /* this is the different bit */
            .transition()
            .attr("x", (_d: number, i: number) => (xScale(i + 1)! - 0.5))
            .attr("y", (d: number) => yScale(d)!)
            .attr("height", (d: number) => (yScale(minDb)! - yScale(d)!))
    }, [xScale, yScale]);

    let title: React.ReactElement | null = null;
    if (props.name) {
        title = <text x={width - margin.right - 5} y={22} className="title">
            {props.name}
        </text>;

        if (props.url) {
            title = <a href={props.url}>
                {title}
            </a>
        }
    }

    const onPointerCancel = React.useCallback((ev: React.PointerEvent<SVGGElement>) => {
        d3.select(ev.currentTarget.parentElement).select(".popup").style("display", "none");
    }, []);

    const onPointerMove = React.useCallback((ev: React.PointerEvent<SVGGElement>) => {
        const [mouseX] = (d3 as any).pointer(ev);
        const harmonic = Math.round(xScale.invert(mouseX));
        const dB = props.harmonics[harmonic - 1];
        let yPos = yScale(dB);
        if (yPos === undefined) {
            return onPointerCancel(ev);
        }
        yPos -= 0.5;

        const popup = d3.select(ev.currentTarget.parentElement).select(".popup")
            .data([null])
            .join("g")
            .style("display", null)
            .style("pointer-events", "none")
            .style("font", "8px sans");

        const path = popup.selectAll("path")
            .data([null])
            .join("path")
            .attr("fill", "#222222")
            .attr("stroke", "#454545");

        const text = popup.selectAll("text")
            .data([null])
            .join("text")
            .attr("fill", "lightgray")
            .attr("text-anchor", "middle");

        text.selectAll("tspan")
            .data([`n = ${harmonic}`, `+${(Math.log2(harmonic)*12).toFixed(1)} st`, formatDecibels(dB) ])
            .join("tspan")
            .attr("x", 0)
            .attr("y", (_, i) => `${i * 1.1}em`)
            .text(d => d);

        const {width: w, height: h} = (text.node() as SVGTextElement).getBBox();

        text.attr("transform", `translate(0,${-h - 2})`);
        // `M${-w / 2 - 5},5H-5l5,-5l5,5H${w / 2 + 5}v${h + 10}h-${w + 10}z`
        path.attr("d", `M0,0l4,-4h${w/2}v-${h + 4}a4,4,0,0,0,-4,-4h-${w}a4,4,0,0,0,-4,4v${h + 4}H-4z`);

        /* Move the whole thing if it would stick off the top of the chart */
        yPos = Math.max(yPos, h + 16);
        popup.attr("transform", `translate(${xScale(harmonic)! + 0.5}, ${yPos})`);

    }, [xScale, props.harmonics, yScale, onPointerCancel]);

    return <svg className="spectrum" viewBox={`0 0 ${width} ${height}`}>
        <g className="y-axis" ref={yRef}>
            <text x={-margin.left} y={margin.top} fill="currentColor" textAnchor="start">dB</text>
        </g>
        <g className="x-axis" ref={xRef}>
            <text x={width} y={margin.bottom} fill="currentColor" textAnchor="end">
                {horizontalScale === HorizontalScale.harmonics ? "harmonic num" : "semitones"}
            </text>
        </g>
        <g ref={chartContent}
           onPointerMove={onPointerMove} onPointerCancel={onPointerCancel} onPointerOut={onPointerCancel}
           pointerEvents="visible">
            { /* invisible rect so that we get pointer events for the whole chart area */ }
            <rect
                x={margin.left}
                y={margin.top}
                width={width - margin.left - margin.right + 1}
                height={height - margin.top - margin.bottom}
                fill="none" stroke="none"
            />
        </g>
        <g className="popup" />
        { title }
    </svg>;
}